// components/UserProfile.tsx
import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styles from './UserProfile.module.css';
import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { API_BASE } from "../../config/api";

interface Product {
  id: number;
  name: string;
  description: string;
  price: number;
  type: string;
  filePath: string;
}

interface Order {
  id: number;
  products?: Product[] | null;
  createdAt?: string | null;
}

type CustomerType = 'B2C' | 'B2B';

interface User {
  id: number;
  customerType: CustomerType;

  salutation?: string;
  firstName?: string;
  lastName?: string;
  birthdate?: string;

  street?: string;
  houseNumber?: string;
  city?: string;
  postalCode?: string;
  federalState?: string;
  country?: string;

  customerNumber?: string;

  email: string;
  phoneCountryCode?: string;
  phone?: string;

  // B2B only
  companyName?: string;
  vatId?: string;
  contactFirstName?: string;
  contactLastName?: string;

  orders?: Order[] | null;
}


interface UserUpdateData {
  customerType?: CustomerType;

  salutation?: string;
  firstName?: string;
  lastName?: string;
  birthdate?: string;

  street?: string;
  houseNumber?: string;
  city?: string;
  postalCode?: string;
  federalState?: string;
  country?: string;

  email?: string;
  phoneCountryCode?: string;
  phone?: string;

  // B2B
  companyName?: string;
  vatId?: string;
  contactFirstName?: string;
  contactLastName?: string;

  // Passwort
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
}

interface PasswordValidation {
  length: boolean;
  uppercase: boolean;
  lowercase: boolean;
  number: boolean;
  special: boolean;
  match: boolean;
}



async function fetchWithAuth(path: string, init: RequestInit = {}): Promise<Response> {
  const finalInit: RequestInit = {
    ...init,
    credentials: 'include',
    headers: {
      "Content-Type": "application/json",
      ...(init.headers || {})
    }
  };

  let res = await fetch(API_BASE + path, finalInit);

  if (res.status === 401) {
    const refresh = await fetch(API_BASE + "/api/auth/refresh", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" }
    });

    if (refresh.ok) {
      res = await fetch(API_BASE + path, finalInit);
    }
  }

  return res;
}

const UserProfile: React.FC = () => {
  //const { userId, id } = useParams<{ userId?: string; id?: string }>();
  const { user: currentUser, logout } = useAuth();
  const navigate = useNavigate();

  // Automatisch zur eigenen Profilseite navigieren wenn keine ID angegeben
  /*
  useEffect(() => {
    if (!userId && !id && currentUser) {
      navigate(`/users/${currentUser.id}`, { replace: true });
    }
  }, [userId, id, currentUser, navigate]);
*/

//const targetId = useMemo(() => {
 // return userId ?? id ?? (currentUser ? String(currentUser.id) : undefined);
//}, [userId, id, currentUser]);
const targetId = currentUser?.id;
//console.log("hallo "+targetId);

  const [activeTab, setActiveTab] = useState<'licenses' | 'settings'>('licenses');
  const [user, setUser] = useState<User | null>(null);

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [updateSuccess, setUpdateSuccess] = useState<string>("");
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [formData, setFormData] = useState<UserUpdateData>({});
  const [deleteConfirm, setDeleteConfirm] = useState<boolean>(false);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  
  // Passwort-Validierungs-State
  const [passwordTouched, setPasswordTouched] = useState<boolean>(false);
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState<boolean>(false);

  // Passwort-Validierung
  const passwordValidation: PasswordValidation = {
    length: (formData.newPassword?.length || 0) >= 8,
    uppercase: /[A-Z]/.test(formData.newPassword || ''),
    lowercase: /[a-z]/.test(formData.newPassword || ''),
    number: /[0-9]/.test(formData.newPassword || ''),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(formData.newPassword || ''),
    match: formData.newPassword === formData.confirmPassword
  };

  const isPasswordValid = 
    passwordValidation.length &&
    passwordValidation.uppercase &&
    passwordValidation.lowercase &&
    passwordValidation.number &&
    passwordValidation.special &&
    (!formData.newPassword || (formData.newPassword && passwordValidation.match));

  useEffect(() => {
if (!targetId) return;
// ${API_BASE}
    const controller = new AbortController();
    const fetchUser = async () => {
      try {
        const res = await fetchWithAuth(`/api/users/${targetId}`, {
          method: 'GET',
          signal: controller.signal
        });

        if (!res.ok) {
          if (res.status === 404) {
            throw new Error("User not found");
          }
          throw new Error(`Error loading (HTTP ${res.status})`);
        }

        const data: User = await res.json();
        setUser({ ...data, orders: data.orders ?? [] });
        setFormData({
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          email: data.email || '',
          phone: data.phone || '',
          street: data.street || '',
          houseNumber: data.houseNumber || '',
          postalCode: data.postalCode || '',
          birthdate: data.birthdate || '',
          federalState: data.federalState || '',
          country: data.country || '',
          companyName: data.companyName || '',
          vatId: data.vatId || '',
          contactFirstName: data.contactFirstName || '',
          contactLastName: data.contactLastName || '',
            city: data.city || '',
        });
      } catch (e: any) {
        if (e?.name === 'AbortError') return;
        setError(e instanceof Error ? e.message : "Unknown error");
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
    return () => controller.abort();
  }, [targetId]);



  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Zurücksetzen der Passwort-Validierung wenn Passwort-Felder geändert werden
    if (name === 'newPassword') {
      setPasswordTouched(true);
      if (formData.confirmPassword) {
        setConfirmPasswordTouched(true);
      }
    }
    if (name === 'confirmPassword') {
      setConfirmPasswordTouched(true);
    }
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!targetId) return;

    // Validierung vor dem Senden
    if (formData.newPassword) {
      if (!isPasswordValid) {
        setError("Please meet all password requirements.");
        return;
      }
      
      // confirmPassword aus dem Payload entfernen, da Backend es nicht erwartet
      const { confirmPassword, ...updateData } = formData;
      
      setError("");
      setUpdateSuccess("");

      try {
        const res = await fetchWithAuth(`/api/users/${targetId}`, {
          method: 'PUT',
          body: JSON.stringify(updateData)
        });

        let data: any = null;
        try {
          data = await res.json();
        } catch {
          // Kein JSON vorhanden, ignorieren
        }

        if (!res.ok) {
          const message = data?.error || data?.message || "Update failed";
          setError(message);
          return;
        }

        const updatedUser: User = data;
        setUser(updatedUser);
        setUpdateSuccess("Update successful");
        setIsEditing(false);
        
        // Passwort-Felder zurücksetzen
        setFormData(prev => ({
          ...prev,
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        }));
        setPasswordTouched(false);
        setConfirmPasswordTouched(false);

        setTimeout(() => setUpdateSuccess(""), 3000);
      } catch (err: any) {
        setError(err instanceof Error ? err.message : "Error during update");
      }
    } else {
      // Kein neues Passwort, einfach nur update
      setError("");
      setUpdateSuccess("");

      try {
        const { confirmPassword, ...updateData } = formData;
        
        const res = await fetchWithAuth(`/api/users/${targetId}`, {
          method: 'PUT',
          body: JSON.stringify(updateData)
        });

        let data: any = null;
        try {
          data = await res.json();
        } catch {
          // Kein JSON vorhanden, ignorieren
        }

        if (!res.ok) {
          const message = data?.error || data?.message || "Update failed";
          setError(message);
          return;
        }

        const updatedUser: User = data;
        setUser(updatedUser);
        setUpdateSuccess("Update successful");
        setIsEditing(false);
        
        setTimeout(() => setUpdateSuccess(""), 3000);
      } catch (err: any) {
        setError(err instanceof Error ? err.message : "Update failed");
      }
    }
  };

  const handleDeleteAccount = async () => {
    if (!targetId || !deleteConfirm) return;

    setDeleteLoading(true);
    try {
      const res = await fetchWithAuth(`/api/users/${targetId}`, {
        method: 'DELETE'
      });

      if (!res.ok) {
        throw new Error(`Failure during deletion (HTTP ${res.status})`);
      }

      if (currentUser && currentUser.id === targetId){
        await logout();
        navigate('/');
      } else {
        navigate('/users');
      }
    } catch (err: any) {
      setError(err instanceof Error ? err.message : "Failure during deletion");
    } finally {
      setDeleteLoading(false);
      setDeleteConfirm(false);
    }
  };


//console.log(API_BASE);
  if (loading) return <p>Loading user data</p>;
  if (error) return <p style={{ color: "red" }}>{error}</p>;
  if (!user) return <p>No user data available</p>;

  const safeOrders = user.orders ?? [];
  const isOwnProfile = currentUser && String(currentUser.id) === targetId;
  const isB2B = user.customerType === 'B2B';
  
  return (
    <div className={styles.profileContainer}>
      <aside className={styles.userInfoBox}>
        <h2>User Data</h2>

        {!isB2B && (
          <>
          <p> <strong>Customer No.:</strong> {user.customerNumber}</p>
            <p>
          
              <strong>Name:</strong> {user.firstName} {user.lastName}
            </p>
            <p><strong>Birthdate:</strong> {user.birthdate || '—'}</p>
          </>
        )}

        {isB2B && (
          <>
            <p><strong>Company:</strong> {user.companyName || '—'}</p>
            <p><strong>VAT:</strong> {user.vatId || '—'}</p>
            <p>
              <strong>Contact Person:</strong>{' '}
              {user.salutation ? `${user.salutation} ` : ''}
              {user.contactFirstName} {user.contactLastName}
            </p>
          </>
        )}

        <p><strong>Phone:</strong> {user.phone || '—'}</p>
        <p><strong>E-mail:</strong> {user.email}</p>

        <p>
          <strong>Address:</strong>{' '}
          {user.street
            ? `${user.street} ${user.houseNumber}, ${user.postalCode}, ${user.city}`
            : '—'}
        </p>

        <p><strong>Land:</strong> {user.country || '—'}</p>

        {!isB2B && (
          <p><strong>State:</strong> {user.federalState || '—'}</p>
        )}
      </aside>

      <div className={styles.mainContent}>
        <div className={styles.tabs}>
          <button
            className={activeTab === 'licenses' ? styles.activeTab : ''}
            onClick={() => setActiveTab('licenses')}
          >
            Licenses
          </button>
          <button
            className={activeTab === 'settings' ? styles.activeTab : ''}
            onClick={() => setActiveTab('settings')}
          >
            Settings
          </button>
        </div>

        {activeTab === 'licenses' && (
          <div className={styles.licenseSection}>
            <h2>Products</h2>

            {safeOrders.length === 0 && <p>You haven't purchased any products yet.</p>}

            {safeOrders.map(order => {
              const products = order.products ?? [];
              return (
                <div key={order.id}>
                  {products.length > 0 ? (
                    products.map(product => {
                     
                      return (
                        <div className={styles.productBox} key={product.id}>
                          <div className={styles.productHeader}>
                            <h3>{product.name}</h3>
                            <span className={styles.licenseStatusGreen}>Unlocked</span>
                            <a href={API_BASE+"/api/download/"+ product.filePath} target="_blank" rel="noopener noreferrer">
                              <button className={styles.downloadButton}>Download</button>
                            </a>
                          </div>
                          <p>{product.description}</p>
    
                          <p><strong>Typ:</strong> {product.type}</p>
                          {order.createdAt && (
                            <p><em>Purchased on {new Date(order.createdAt).toLocaleDateString()}</em></p>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <p>No products in this order.</p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {activeTab === 'settings' && (
          <div className={styles.settingsSection}>
            <h2>Settings</h2>

            {updateSuccess && (
              <div className={styles.successMessage}>{updateSuccess}</div>
            )}
            {error && (
              <div className={styles.errorMessage}>{error}</div>
            )}

            <div className={styles.settingsCard}>
              <div className={styles.settingsHeader}>
                <h3>Edit user data</h3>
                {!isEditing && (
                  <button
                    className={styles.editButton}
                    onClick={() => setIsEditing(true)}
                  >
                    Edit
                  </button>
                )}
              </div>

              {isEditing ? (
                <form onSubmit={handleUpdateUser} className={styles.userForm}>
                  <div className={styles.formGrid}>
                    {isB2B && (
                      <>
                        <div className={styles.formGroup}>
                          <label>Firma</label>
                          <input
                            name="companyName"
                            value={formData.companyName || ''}
                            onChange={handleInputChange}
                          />
                        </div>

                        <div className={styles.formGroup}>
                          <label>VAT</label>
                          <input
                            name="vatId"
                            value={formData.vatId || ''}
                            onChange={handleInputChange}
                          />
                        </div>

                        <div className={styles.formGroup}>
                          <label>Contact Person First Name</label>
                          <input
                            name="contactFirstName"
                            value={formData.contactFirstName || ''}
                            onChange={handleInputChange}
                          />
                        </div>

                        <div className={styles.formGroup}>
                          <label>Contact Person Last Name</label>
                          <input
                            name="contactLastName"
                            value={formData.contactLastName || ''}
                            onChange={handleInputChange}
                          />
                        </div>
                      </>
                    )}

                    {!isB2B && (
                      <>
                        <div className={styles.formGroup}>
                          <label>First Name</label>
                          <input
                            type="text"
                            name="firstName"
                            value={formData.firstName || ''}
                            onChange={handleInputChange}
                            required
                          />
                        </div>

                        <div className={styles.formGroup}>
                          <label>Last Name</label>
                          <input
                            type="text"
                            name="lastName"
                            value={formData.lastName || ''}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </>
                    )}
                    
                    <div className={styles.formGroup}>
                      <label>E-mail</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email || ''}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <label>Phone</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone || ''}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <label>Street</label>
                      <input
                        type="text"
                        name="street"
                        value={formData.street || ''}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <label>House No.</label>
                      <input
                        type="text"
                        name="houseNumber"
                        value={formData.houseNumber || ''}
                        onChange={handleInputChange}
                      />
                    </div>
          <div className={styles.formGroup}>
                      <label>City</label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city || ''}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label>Postal Code</label>
                      <input
                        type="text"
                        name="postalCode"
                        value={formData.postalCode || ''}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <label>State</label>
                      <input
                        type="text"
                        name="federalState"
                        value={formData.federalState || ''}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <label>Country</label>
                      <input
                        type="text"
                        name="country"
                        value={formData.country || ''}
                        onChange={handleInputChange}
                      />
                    </div>
                    
                    {!isB2B && (
                      <div className={styles.formGroup}>
                        <label>Birthdate</label>
                        <input
                          type="date"
                          name="birthdate"
                          value={formData.birthdate || ''}
                          onChange={handleInputChange}
                        />
                      </div>
                    )}
                    
                    {/* Passwort-Änderungsbereich */}
                    <div className={styles.passwordSection}>
                      <h3>Change Password</h3>
                      
                      <div className={styles.formGroup}>
                        <label>Current Password</label>
                        <input
                          type="password"
                          name="currentPassword"
                          value={formData.currentPassword || ''}
                          onChange={handleInputChange}
                          placeholder="Only fill out if you want to change your password."
                        />
                      </div>

                      <div className={styles.formGroup}>
                        <label>New Password</label>
                        <input
                          type="password"
                          name="newPassword"
                          value={formData.newPassword || ''}
                          onChange={handleInputChange}
                          onFocus={() => setPasswordTouched(true)}
                        />
                      </div>

                      {/* Passwort-Regeln anzeigen */}
                      {passwordTouched && formData.newPassword && (
                        <ul className={styles.passwordRules}>
                          <li className={passwordValidation.length ? styles.ok : styles.error}>
                            • At least 8 characters
                          </li>
                          <li className={passwordValidation.uppercase ? styles.ok : styles.error}>
                            • At least one uppercase letter
                          </li>
                          <li className={passwordValidation.lowercase ? styles.ok : styles.error}>
                            • At least one lowercase letter
                          </li>
                          <li className={passwordValidation.number ? styles.ok : styles.error}>
                            • At least one number
                          </li>
                          <li className={passwordValidation.special ? styles.ok : styles.error}>
                            • At least one special character (!@#$%^&*(),.?":{}|&lt;&gt;)
                          </li>
                        </ul>
                      )}

                      <div className={styles.formGroup}>
                        <label>Repeat Password</label>
                        <input
                          type="password"
                          name="confirmPassword"
                          value={formData.confirmPassword || ''}
                          onChange={handleInputChange}
                          onFocus={() => setConfirmPasswordTouched(true)}
                        />
                      </div>

                      {/* Passwort-Übereinstimmungs-Prüfung */}
                      {confirmPasswordTouched && formData.confirmPassword && (
                        <div className={passwordValidation.match ? styles.passwordMatch : styles.passwordMismatch}>
                          {passwordValidation.match ? '✓ Passwords matching' : '✗ Passwords not matching'}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={styles.formActions}>
                    <button 
                      type="submit" 
                      className={styles.saveButton}
                      disabled={!!formData.newPassword && !isPasswordValid}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className={styles.cancelButton}
                      onClick={() => {
                        setIsEditing(false);
                        setPasswordTouched(false);
                        setConfirmPasswordTouched(false);
                        setFormData({
                          firstName: user.customerType === 'B2C' ? user.firstName || '' : '',
                          lastName: user.customerType === 'B2C' ? user.lastName || '' : '',
                          email: user.email || '',
                          phone: user.phone || '',
                          street: user.street || '',
                          houseNumber: user.houseNumber || '',
                          postalCode: user.postalCode || '',
                          federalState: user.federalState || '',
                          city: user.city|| '',
                          country: user.country || '',
                          birthdate: user.customerType === 'B2C' ? user.birthdate || '' : '',
                          salutation: user.customerType === 'B2B' ? user.salutation || '' : '',
                          companyName: user.customerType === 'B2B' ? user.companyName || '' : '',
                          
                          vatId: user.customerType === 'B2B' ? user.vatId || '' : '',
                          contactFirstName: user.customerType === 'B2B' ? user.contactFirstName || '' : '',
                          contactLastName: user.customerType === 'B2B' ? user.contactLastName || '' : '',
                          currentPassword: '',
                          newPassword: '',
                          confirmPassword: ''
                        });
                      }}
                    >
                      Abort
                    </button>
                  </div>
                </form>
              ) : (
                <div className={styles.userDataDisplay}>
                  {!isB2B && (
                    <>
                      <p><strong>First Name:</strong> {user.firstName}</p>
                      <p><strong>Last Name:</strong> {user.lastName}</p>
                      <p><strong>Birthdate:</strong> {user.birthdate || '—'}</p>
                    </>
                  )}

                  {isB2B && (
                    <>
                      <p><strong>Company:</strong> {user.companyName || '—'}</p>
                      <p><strong>VAT:</strong> {user.vatId || '—'}</p>
                      <p>
                        <strong>Contact Person:</strong>{' '}
                        {user.salutation ? `${user.salutation} ` : ''}
                        {user.contactFirstName} {user.contactLastName}
                      </p>
                    </>
                  )}

                  <p><strong>E-mail:</strong> {user.email}</p>
                  <p><strong>Phone:</strong> {user.phone || '—'}</p>

                  <p>
                    <strong>Address:</strong>{' '}
                    {user.street
                      ? `${user.street} ${user.houseNumber},${user.city}, ${user.postalCode}`
                      : '—'}
                  </p>

                  {!isB2B && (
                    <p><strong>State:</strong> {user.federalState || '—'}</p>
                  )}

                  <p><strong>Coutry:</strong> {user.country || '—'}</p>
                </div>
              )}
            </div>

            {/* Account Löschen Bereich */}
            { (
              <div className={styles.dangerZone}>
                <h3>Delete Account</h3>
                <p>This action cannot be undone. Your account and all associated data will be permanently deleted.</p>

                {!deleteConfirm ? (
                  <button
                    className={styles.deleteButton}
                    onClick={() => setDeleteConfirm(true)}
                  >
                    Delete Account
                  </button>
                ) : (
                  <div className={styles.deleteConfirmation}>
                    <p><strong>Are you sure you want to delete your account?</strong></p>
                    <div className={styles.deleteActions}>
                      <button
                        className={styles.confirmDeleteButton}
                        onClick={handleDeleteAccount}
                        disabled={deleteLoading}
                      >
                        {deleteLoading ? 'Deleting...' : 'Yes, delete account'}
                      </button>
                      <button
                        className={styles.cancelDeleteButton}
                        onClick={() => setDeleteConfirm(false)}
                        disabled={deleteLoading}
                      >
                        Abort
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfile;